import { useCallback, useEffect, useRef, useState } from "react";

const START = {
  status: "loading",
  items: [],
  nextCursor: null,
  firstPage: null,
  message: "",
  code: undefined,
  loadingMore: false,
  moreError: "",
};

// A list the service pages with an opaque `nextCursor`. `fetchPage(cursor)` must be stable (wrap it
// in useCallback) and return the { ok, data: { items, nextCursor }, message, code } of warrantyApi.
// A newer reload drops the answer of an older one, so a fast change of filter never shows old rows.
// `firstPage` keeps the whole `data` of the first page (for totals a service sends only there).
export const usePagedList = (fetchPage, enabled = true) => {
  const [state, setState] = useState(START);
  const stateRef = useRef(START);
  const seq = useRef(0);
  const mounted = useRef(true);

  const put = useCallback((next) => {
    stateRef.current = typeof next === "function" ? next(stateRef.current) : next;
    setState(stateRef.current);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // `quiet` keeps the rows on screen while the list is read again (after an action).
  const reload = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (!quiet) {
        put({ ...START });
      }
      const result = await fetchPage(null);
      if (!mounted.current || mine !== seq.current) {
        return;
      }
      if (result.ok) {
        put({
          ...START,
          status: "ready",
          items: result.data?.items || [],
          nextCursor: result.data?.nextCursor || null,
          firstPage: result.data || null,
        });
      } else if (quiet && stateRef.current.status === "ready") {
        put((prev) => ({ ...prev, moreError: result.message }));
      } else {
        put({ ...START, status: "error", message: result.message, code: result.code });
      }
    },
    [fetchPage, put]
  );

  useEffect(() => {
    if (enabled) {
      reload(false);
    }
  }, [enabled, reload]);

  const loadMore = useCallback(async () => {
    const cursor = stateRef.current.nextCursor;
    if (!cursor || stateRef.current.loadingMore) {
      return;
    }
    const mine = seq.current;
    put((prev) => ({ ...prev, loadingMore: true, moreError: "" }));
    const result = await fetchPage(cursor);
    if (!mounted.current || mine !== seq.current) {
      return;
    }
    if (result.ok) {
      put((prev) => ({
        ...prev,
        loadingMore: false,
        items: [...prev.items, ...(result.data?.items || [])],
        nextCursor: result.data?.nextCursor || null,
      }));
    } else {
      put((prev) => ({ ...prev, loadingMore: false, moreError: result.message }));
    }
  }, [fetchPage, put]);

  const removeWhere = useCallback(
    (test) => put((prev) => ({ ...prev, items: prev.items.filter((item) => !test(item)) })),
    [put]
  );

  return { ...state, reload, loadMore, removeWhere };
};
